import { useRef } from "react";
import PropTypes from "prop-types";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { usePersonaStore } from "@/store/usePersonaStore";

const ACCEPTED_IMAGE_TYPES = "image/png,image/jpeg,image/jpg,image/webp,image/gif";

function logPersonaAvatar(event, detail = {}) {
  if (process.env.NODE_ENV !== "development") return;
  // eslint-disable-next-line no-console
  console.info(`[persona-avatar] ${event}`, detail);
}

function getAvatarButtonAttributes({ disabled, canUpload, hasCustom }) {
  let title = "Click Edit to change your Persona photo";
  if (disabled) {
    title = "Admins can upload a custom photo";
  } else if (canUpload) {
    title = hasCustom ? "Click to replace photo" : "Click to upload a custom photo";
  }

  let ariaLabel = "Persona photo";
  if (canUpload) {
    ariaLabel = hasCustom ? "Replace photo" : "Upload photo";
  }
  return { title, ariaLabel };
}

function renderAvatarContent({ avatarPreviewUrl, avatarKind, initial, size }) {
  if (avatarPreviewUrl) {
    return <img src={avatarPreviewUrl} alt="" className="h-full w-full object-cover" />;
  }
  if (avatarKind === "uploaded") {
    return <span className="font-semibold" style={{ fontSize: size * 0.23 }}>{initial}</span>;
  }
  return <Sparkles size={size * 0.33} strokeWidth={2.25} />;
}

/**
 * Persona profile photo control.
 *
 * @param {boolean} disabled — no permission (view-only role)
 * @param {boolean} editable — page is in Edit mode; upload/remove only when true
 * @param {function} [onLockedClick] — called when permitted user clicks while not editing
 */
export function PersonaAvatarButton({
  name,
  disabled = false,
  editable = true,
  onLockedClick,
  onFileChosen,
  size = 96,
  className,
  testId = "persona-avatar-upload",
  inputTestId = "persona-avatar-input",
  removeTestId = "persona-avatar-remove",
}) {
  const inputRef = useRef(null);
  const {
    avatarKind,
    avatarPreviewUrl,
    avatarPendingClear,
    setAvatarFromFile,
    clearAvatar,
    hasCustomAvatar,
  } = usePersonaStore();
  const canUpload = !disabled && editable;
  const hasCustom = !avatarPendingClear && hasCustomAvatar();

  const onFileSelected = (file) => {
    if (!file || !canUpload) return;
    logPersonaAvatar(hasCustom ? "replace_initiated" : "upload_initiated", {
      name: file.name,
      size: file.size,
      type: file.type,
    });
    const result = setAvatarFromFile(file);
    if (result?.ok === false) {
      toast.error(result.error || "Couldn't use that image");
      return;
    }
    onFileChosen?.(file);
    logPersonaAvatar("avatar_changed", { label: file.name });
    toast.success("Photo selected — click Save to sync across devices");
  };

  const handleClick = () => {
    if (disabled) return;
    if (!editable) {
      logPersonaAvatar("blocked_readonly");
      if (onLockedClick) onLockedClick();
      else toast.message("Click Edit to modify your Persona.");
      return;
    }
    logPersonaAvatar("picker_opened");
    inputRef.current?.click();
  };

  const handleRemove = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!canUpload || !hasCustom) return;
    clearAvatar();
    onFileChosen?.(null);
    logPersonaAvatar("avatar_removed");
    toast.success("Default photo restored — click Save to sync across devices");
  };

  const initial = (name || "M").trim()[0] || "M";
  const { title, ariaLabel } = getAvatarButtonAttributes({ disabled, canUpload, hasCustom });

  return (
    <div className={cn("flex flex-col items-center gap-1.5 shrink-0", className)}>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_IMAGE_TYPES}
        className="hidden"
        data-testid={inputTestId}
        disabled={!canUpload}
        tabIndex={-1}
        onChange={(e) => {
          onFileSelected(e.target.files?.[0]);
          e.target.value = "";
        }}
      />
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled || !editable ? "true" : undefined}
        className={cn(
          "group relative shrink-0",
          disabled && "opacity-60 cursor-not-allowed",
          !disabled && !editable && "cursor-default",
        )}
        data-testid={testId}
        title={title}
        aria-label={ariaLabel}
      >
        <div
          className="rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 text-white flex items-center justify-center shadow-md ring-4 ring-white overflow-hidden"
          style={{ width: size, height: size }}
        >
          {renderAvatarContent({ avatarPreviewUrl, avatarKind, initial, size })}
        </div>
        {canUpload && (
          <div className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity text-white text-[10px] flex items-center justify-center font-medium text-center px-1">
            {hasCustom ? "Replace" : "Upload"}
          </div>
        )}
      </button>
      {canUpload && hasCustom && (
        <button
          type="button"
          onClick={handleRemove}
          className="text-[11px] font-medium text-slate-500 hover:text-rose-700 transition-colors underline-offset-2 hover:underline min-h-8 sm:min-h-0 px-1"
          data-testid={removeTestId}
          aria-label="Reset to default photo"
        >
          Reset to default
        </button>
      )}
    </div>
  );
}

PersonaAvatarButton.propTypes = {
  name: PropTypes.string,
  disabled: PropTypes.bool,
  editable: PropTypes.bool,
  onLockedClick: PropTypes.func,
  onFileChosen: PropTypes.func,
  size: PropTypes.number,
  className: PropTypes.string,
  testId: PropTypes.string,
  inputTestId: PropTypes.string,
  removeTestId: PropTypes.string,
};
